import { DataTable } from "mantine-datatable";
import { useEffect, useState } from "react";

import Tippy from "@tippyjs/react";
import ImageCustom from "../../../components/custom/image-custom";
import { LabelStatusCustom } from "../../../components/custom/label-status-custom";
import {
  IconActive,
  IconEdit,
  IconEye,
  IconReset,
  IconUnActive,
  IconUserDefault,
} from "../../../components/icons/icon-group";
import { CHANGE_STATUS, PAGE_SIZES, STATUS } from "../../../constants/status";
import { EmployeeResponse } from "../../../models/response/employee";
import CookieUtils from "../../../utils/cookie-utils";
import { AdminFilter } from "./admin-filter";

interface AdminTableProps {
  loadingTable: boolean;
  dataAdmin: EmployeeResponse[];
  page: number;
  setPage: (value: number) => void;
  limit: number;
  setLimit: (value: number) => void;
  keySearch: string;
  setKeySearch: (value: string) => void;
  totalRecords: number;
  setOpenModalUpdate: (value: boolean) => void;
  setOpenModalDetail: (value: boolean) => void;
  setOpenModalChangeStatus: (value: boolean) => void;
  setOpenModalResetPass: (value: boolean) => void;
  setDataObject: (value: EmployeeResponse) => void;
}

const AdminTable = (props: AdminTableProps) => {
  const [tableMaxHeight, setTableMaxHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const header = document.querySelector(".navbar") as HTMLElement;
      const filter = document.getElementById("filter-bar") as HTMLElement;
      const tab = document.getElementById("tab-menu") as HTMLElement;
      if (header && filter && tab) {
        const headerHeight = header.offsetHeight;
        const filterHeight = filter.offsetHeight;
        const tabHeight = tab.offsetHeight;
        const remainingHeight = window.innerHeight - headerHeight - tabHeight - filterHeight - 24 - 24 - 40 - 24;
        setTableMaxHeight(remainingHeight);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChangeStatus = (id: string, typeAdmin: number) => {
    const selectedData = props.dataAdmin.find((item) => item._id === id) as EmployeeResponse;
    props.setDataObject(selectedData);
    switch (typeAdmin) {
      case CHANGE_STATUS.UPDATE:
        props.setOpenModalUpdate(true);
        break;
      case CHANGE_STATUS.ACTIVE:
      case CHANGE_STATUS.INACTIVE:
        props.setOpenModalChangeStatus(true);
        break;
      case CHANGE_STATUS.RESET:
        props.setOpenModalResetPass(true);
        break;
      case CHANGE_STATUS.DETAIL:
        props.setOpenModalDetail(true);
        break;
      default:
        break;
    }
  };

  return (
    <div className="panel flex flex-col gap-3">
      <AdminFilter searchValue={props.keySearch} setSearchValue={props.setKeySearch} />
      <div className="datatables" style={{ height: tableMaxHeight }}>
        <DataTable
          fetching={props.loadingTable}
          withColumnBorders
          noRecordsText="Chưa có dữ liệu"
          highlightOnHover
          className="whitespace-nowrap table-hover"
          records={props.dataAdmin}
          columns={[
            {
              accessor: "",
              title: "STT",
              render: (record, index) => <div>{(props.page - 1) * props.limit + index + 1}</div>,
            },
            {
              accessor: "name",
              title: "Tên nhân viên",
              render: ({ name, avatar }) => (
                <div className="flex items-center w-max">
                  <ImageCustom
                    isReview
                    fallBackUrl={IconUserDefault}
                    url={`${avatar || IconUserDefault}`}
                    isLocal={false}
                    className="w-9 h-9 rounded-full ltr:mr-2 rtl:ml-2 object-cover"
                  />
                  <div>{name}</div>
                </div>
              ),
            },

            { accessor: "phone", title: "Số điện thoại" },

            {
              accessor: "status",
              title: "Trạng thái",
              titleClassName: "!text-center",
              render: ({ status }) => (
                <LabelStatusCustom
                  content={status === 1 ? "Đang hoạt động" : "Tạm ngưng"}
                  backgroundColor={status ? "bg-green-200" : "bg-red-200"}
                  textColor={status ? "text-green-600" : "text-red-600"}
                />
              ),
              width: 150,
            },
            {
              accessor: "action",
              title: "Action",
              titleClassName: "!text-center",
              render: ({ status, _id }) => {
                return (
                  <div className="flex items-center w-max mx-auto gap-2">
                    {CookieUtils.getCurrentUser()._id != _id && (
                      <>
                        <Tippy content="Reset mật khẩu">
                          <button
                            disabled={status === STATUS.INACTIVE}
                            type="button"
                            onClick={() => {
                              handleChangeStatus(_id, CHANGE_STATUS.RESET);
                            }}
                            className={`${status === STATUS.INACTIVE ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            <IconReset />
                          </button>
                        </Tippy>
                        <Tippy content="Chỉnh sửa">
                          <button
                            disabled={status === STATUS.INACTIVE}
                            type="button"
                            onClick={() => {
                              handleChangeStatus(_id, CHANGE_STATUS.UPDATE);
                            }}
                            className={`${status === STATUS.INACTIVE ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            <IconEdit />
                          </button>
                        </Tippy>
                        <Tippy content="Chi tiết">
                          <button
                            type="button"
                            onClick={() => {
                              handleChangeStatus(_id, CHANGE_STATUS.DETAIL);
                            }}
                          >
                            <IconEye />
                          </button>
                        </Tippy>

                        {status === STATUS.ACTIVE ? (
                          <Tippy content="Tạm ngưng">
                            <button
                              type="button"
                              onClick={() => {
                                handleChangeStatus(_id, CHANGE_STATUS.INACTIVE);
                              }}
                            >
                              <IconUnActive />
                            </button>
                          </Tippy>
                        ) : (
                          <Tippy content="Bật hoạt động">
                            <button
                              type="button"
                              onClick={() => {
                                handleChangeStatus(_id, CHANGE_STATUS.ACTIVE);
                              }}
                            >
                              <IconActive />
                            </button>
                          </Tippy>
                        )}
                      </>
                    )}
                  </div>
                );
              },
            },
          ]}
          totalRecords={props.totalRecords}
          recordsPerPage={props.limit}
          page={props.page}
          onPageChange={(p) => props.setPage(p)}
          recordsPerPageOptions={PAGE_SIZES}
          onRecordsPerPageChange={props.setLimit}
          minHeight={200}
          paginationText={({ from, to, totalRecords }) => `Hiển thị ${from} đến ${to} của ${totalRecords} hàng`}
        />
      </div>
    </div>
  );
};

export default AdminTable;
